import { errorHandler } from '@/services'
import accountService from '@/services/accountService'
import type Instance from '@/models/Instance'

export const getFileHandler = async (filename: string): Promise<FileSystemFileHandle | null> => {
  try {
    await navigator.storage.persist()
    const root = await navigator.storage.getDirectory()
    const diagsDirectory = await root.getDirectoryHandle("diagnostics", { create: true })
    return await diagsDirectory.getFileHandle(filename, { create: true })
  } catch (err) {
    console.error("Failed to get OPFS file handler", err)
  }

  return null
}

export const cleanupLocalFile = async (filename: string) => {
  try {
    const root = await navigator.storage.getDirectory()
    const diagsDirectory = await root.getDirectoryHandle("diagnostics")
    await diagsDirectory.removeEntry(filename)
  } catch (err: any) {
    console.error("Failed to remove local files", err)
  }
}

export const getDiagsUploadURL = async (instance: Instance): Promise<string | undefined> => {
  try {
    const { jwt } = await accountService.getInstanceLicenseInfo(instance.id)
    false && console.log("Generating signed URL for diagnostics upload")
    const lmDomain = useRuntimeConfig()?.public?.lm?.domain || 'lm.yellowbrick.com'
    const domain = `observability.${lmDomain}`
    const ns = instance.namespaceName ?? ''
    const name = instance.name ?? ''
    const url = `https://${domain}/generate-upload-url?instance_id=${instance.id}&instance_namespace=${encodeURIComponent(ns)}&instance_name=${encodeURIComponent(name)}`
    const response = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${jwt}` }
    })
    if (response.ok) {
      const { uploadURL } = await response.json()
      return uploadURL
    } else {
      console.error(`Error ${response.status} communicating with observability endpoint for upload URL`)
    }
  } catch (err) {
    console.error(err)
    errorHandler.thrown(err, 'Unable to upload diagnostics to Yellowbrick support')
  }
}

export const downloadDiagnostics = async (fileHandle: FileSystemFileHandle, downloadURL: string) => {
  try {
    const writable = await fileHandle.createWritable()
    const downloadRes = await fetch(downloadURL)
    if (!downloadRes.ok) {
      throw new Error(`Failed to download diagnostics file: ${downloadRes.status} ${downloadRes.statusText}`)
    }

    await downloadRes.body!.pipeTo(writable)
    return true
  } catch (err) {
    errorHandler.thrown(err, 'Unable to download diagnostics file')
    return false
  }
}

export const uploadDiagnostics = async (fileHandle: FileSystemFileHandle, uploadURL: string): Promise<boolean> => {
  try {
    const file = await fileHandle.getFile()
    false && console.log('Uploading file', file, 'to', uploadURL)
    const uploadRes = await fetch(uploadURL, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": "application/tar+gzip"
      }
    })
    if (!uploadRes.ok) {
      throw new Error(`Upload failed: ${uploadRes.status} ${uploadRes.statusText}`)
    }
    false && console.log('Successfully uploaded file', file, 'to', uploadURL)
    return true
  } catch (err) {
    console.error(err)
    errorHandler.thrown(err, 'Unable to upload diagnostics to Yellowbrick support')
  } finally {
    cleanupLocalFile(fileHandle.name)
  }

  return false
}
